import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '../../design-system/components/Logo';
import { Tabs } from '../../design-system/components/Tabs';
import { useAuth } from '../../features/auth/useAuth';
import { ClaimBalk } from '../../features/claim/ClaimBalk';
import { useLijsten } from '../../lib/useLijsten';
import { useOnline } from '../../lib/useOnline';

const TAB_NAAR_PAD: Record<string, string> = {
  lijst: '/',
  voorraad: '/voorraad',
};

function tabVoorPad(pad: string): string {
  return pad.startsWith('/voorraad') ? 'voorraad' : 'lijst';
}

// Eén keer bij het laden, net als in Wagenpark: de datum in de balk hoeft niet
// mee te veranderen zolang het tabblad openstaat.
const vandaag = new Date().toLocaleDateString('nl-NL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/**
 * De balk, de tabs en wat op elke tab hoort te staan.
 *
 * De claimbalk en de offlinemelding staan hier en niet op één van de tabs: "wie
 * gaat er vandaag" en "je hebt geen verbinding" gelden voor de hele app, en je
 * hoort ze niet kwijt te raken door naar de voorraad te klikken.
 */
export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const online = useOnline();
  const { items, voorraad } = useLijsten();

  const aantalOpen = items.data?.items.filter((i) => !i.gedaan).length ?? 0;
  const aantalGedaan = items.data?.items.filter((i) => i.gedaan).length ?? 0;
  const aantalVoorraad = voorraad.data?.voorraad.length ?? 0;
  // Wat aandacht vraagt: producten die op of onder hun drempel zitten.
  const aantalLaag =
    voorraad.data?.voorraad.filter((v) => v.minimum !== null && v.aantal <= v.minimum).length ?? 0;

  const actief = tabVoorPad(location.pathname);

  return (
    <>
      <header className="vs-header">
        <div className="vs-header__top">
          <div className="vs-header__brand">
            <Logo height={28} />
            <div className="vs-header__divider" />
            <div className="vs-header__eyebrow">Boodschappen</div>
          </div>
          <div className="vs-header__right">
            <span className="vs-header__date">{vandaag}</span>
            <span className="vs-header__user">{user?.naam}</span>
            <button className="vs-btn vs-btn--ghost vs-btn--sm" onClick={() => void logout()}>
              Uitloggen
            </button>
          </div>
        </div>
        <div className="vs-header__tabs">
          <Tabs
            items={[
              { id: 'lijst', label: 'Boodschappenlijst', aantal: aantalOpen },
              { id: 'voorraad', label: 'Voorraad', aantal: aantalVoorraad },
            ]}
            value={actief}
            onChange={(id) => navigate(TAB_NAAR_PAD[id] ?? '/')}
          />
        </div>
      </header>

      <main className="vs-main">
        {!online && (
          <div className="vs-offline" role="status">
            Geen verbinding. Wat je nu aanvinkt of toevoegt wordt verstuurd zodra je weer online
            bent.
          </div>
        )}

        <ClaimBalk />

        <div className="vs-stats">
          <span>{aantalOpen} open</span>
          <span>{aantalGedaan} klaar</span>
          <span>{aantalVoorraad} voorraad</span>
          {aantalLaag > 0 && <span className="vs-stats__let-op">{aantalLaag} bijna op</span>}
        </div>

        <Outlet />
      </main>
    </>
  );
}
